// Derive each skill's `state` from real progress (localStorage) instead of
// reading the hardcoded value off the static realm data. The static data only
// carries the *gate* ('locked' + `gate: 'lv N'`); every other state ('done',
// 'current', 'todo') is computed here so the UI never claims work the user
// hasn't actually done.

import { problemsBySkillId, problemsByRealmId } from '../data/problems';
import type { Realm, Skill, SkillState } from '../data/realms';
import { getSolvedSet, hasSavedCode } from './progress';

export function deriveSkillState(
  skill: Pick<Skill, 'id' | 'state'>,
  solved: Set<string>,
): SkillState {
  // Alpha: no XP gating — every skill is open to everyone. (Previously
  // `skill.state === 'locked'` short-circuited to 'locked' here.) The static
  // `state: 'locked'` / `gate` fields in realms.ts are now vestigial; state is
  // derived purely from progress. Re-introduce a gate here if gating returns.
  const problems = problemsBySkillId(skill.id);
  if (problems.length === 0) return 'todo';

  let solvedCount = 0;
  for (const p of problems) if (solved.has(p.id)) solvedCount++;

  if (solvedCount >= problems.length) return 'done';
  if (solvedCount > 0) return 'current';

  for (const p of problems) if (hasSavedCode(p.id)) return 'current';
  return 'todo';
}

export function deriveRealmSkills(realm: Realm, solved?: Set<string>): Skill[] {
  const s = solved ?? getSolvedSet();
  return realm.skills.map(skill => ({ ...skill, state: deriveSkillState(skill, s) }));
}

export interface RealmProgress {
  done: number;
  total: number;
}

// `total` counts every skill in the realm (so 3/15 stays 3/15 of the listed
// skills, including XP-gated ones). `done` is the number whose problems are
// all solved per `deriveSkillState`.
export function realmProgress(realm: Realm, solved?: Set<string>): RealmProgress {
  const s = solved ?? getSolvedSet();
  let done = 0;
  for (const skill of realm.skills) {
    if (deriveSkillState(skill, s) === 'done') done++;
  }
  return { done, total: realm.skills.length };
}

// Problem-level progress for the realm — counts individual solved problems
// against every problem tagged to this realm. Used by the island-card badge
// so "3/47" means three problems solved out of 47 in the domain.
export function realmProblemProgress(realm: Realm, solved?: Set<string>): RealmProgress {
  const s = solved ?? getSolvedSet();
  const problems = problemsByRealmId(realm.id);
  let done = 0;
  for (const p of problems) if (s.has(p.id)) done++;
  return { done, total: problems.length };
}
